import { useState, useEffect, useRef } from 'react';
import {
  makeStyles,
  tokens,
  Text,
  Card,
  Input,
  Button,
  Field,
  Spinner,
  Divider,
  MessageBar,
  MessageBarBody,
} from '@fluentui/react-components';
import { Key24Regular, Checkmark24Regular, QrCode24Regular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Html5QrcodeScanner, Html5QrcodeScanType } from 'html5-qrcode';
import { useKiosk } from '../context/KioskContext';
import { useKioskSessionStore } from '../stores/kioskSessionStore';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    padding: tokens.spacingHorizontalXXL,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  card: {
    maxWidth: '600px',
    width: '100%',
    padding: tokens.spacingHorizontalXL,
  },
  header: {
    textAlign: 'center',
    marginBottom: tokens.spacingVerticalXL,
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalM,
  },
  subtitle: {
    color: tokens.colorNeutralForeground2,
  },
  qrSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalL,
  },
  qrSectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground1,
  },
  qrContainer: {
    width: '100%',
    minHeight: '300px',
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
  },
  divider: {
    margin: `${tokens.spacingVerticalL} 0`,
  },
  manualSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  manualSectionTitle: {
    color: tokens.colorNeutralForeground2,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  input: {
    fontSize: tokens.fontSizeBase400,
    fontFamily: 'monospace',
    textAlign: 'center',
    letterSpacing: '2px',
  },
  button: {
    marginTop: tokens.spacingVerticalM,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    textAlign: 'center',
  },
  statusMessage: {
    textAlign: 'center',
    color: tokens.colorNeutralForeground2,
  },
  sessionExpiredBanner: {
    marginBottom: tokens.spacingVerticalL,
  },
});

interface KioskSetupProps {
  onComplete: () => void;
}

interface QrCredentials {
  key: string;
  username: string;
  password: string;
}

export function KioskSetup({ onComplete }: KioskSetupProps) {
  const styles = useStyles();
  const { t } = useTranslation('kiosk');
  const { setKioskKey, setAuthenticated } = useKiosk();
  const expired = useKioskSessionStore((s) => s.expired);
  const clearExpired = useKioskSessionStore((s) => s.clearExpired);

  // Manual key entry state
  const [key, setKey] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // QR scanner state
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [scannerReady, setScannerReady] = useState(false);
  const [scannerStopped, setScannerStopped] = useState(false);
  const qrScannerRef = useRef<Html5QrcodeScanner | null>(null);
  const qrContainerRef = useRef<HTMLDivElement>(null);
  const scanHandledRef = useRef(false);

  const initScanner = () => {
    if (!qrContainerRef.current) return;

    // Clean up existing scanner
    if (qrScannerRef.current) {
      qrScannerRef.current.clear().catch(() => {});
      qrScannerRef.current = null;
    }

    scanHandledRef.current = false;
    setScannerStopped(false);
    setError(null);

    const scannerId = 'kiosk-qr-reader';
    const scanner = new Html5QrcodeScanner(
      scannerId,
      {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA],
        aspectRatio: 1.0,
      },
      /* verbose= */ false
    );

    qrScannerRef.current = scanner;
    setScannerReady(true);

    scanner.render(
      async (decodedText: string) => {
        if (scanHandledRef.current) return;
        scanHandledRef.current = true;

        try {
          const credentials: QrCredentials = JSON.parse(decodedText);
          if (!credentials.key || !credentials.username || !credentials.password) {
            setError(t('setup.errorQrInvalid', 'Invalid QR code format'));
            scanHandledRef.current = false;
            return;
          }

          setIsAuthenticating(true);
          setError(null);

          await scanner.clear();

          await axios.post(
            '/bff/kiosk/auth',
            { key: credentials.key, username: credentials.username, password: credentials.password },
            { withCredentials: true }
          );

          setKioskKey(credentials.key);
          setAuthenticated(true);
          clearExpired();
          onComplete();
        } catch (err) {
          console.error('Kiosk QR authentication failed:', err);
          setIsAuthenticating(false);
          setScannerStopped(true);
          if (axios.isAxiosError(err) && err.response?.status === 401) {
            setError(t('setup.errorQrAuthFailed', 'Authentication failed. Check kiosk credentials.'));
          } else {
            setError(t('setup.errorQrScanFailed', 'QR authentication failed. Try manual key entry.'));
          }
        }
      },
      (errorMessage: string) => {
        void errorMessage;
      }
    );
  };

  useEffect(() => {
    initScanner();

    return () => {
      if (qrScannerRef.current) {
        qrScannerRef.current.clear().catch(() => {});
        qrScannerRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Paste code auth (same as QR scan but from text input)
  const handleCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const credentials: QrCredentials = JSON.parse(codeInput.trim());
      if (!credentials.key || !credentials.username || !credentials.password) {
        setError(t('setup.errorQrInvalid', 'Invalid QR code format'));
        return;
      }

      setIsAuthenticating(true);
      setError(null);

      await axios.post(
        '/bff/kiosk/auth',
        { key: credentials.key, username: credentials.username, password: credentials.password },
        { withCredentials: true }
      );

      setKioskKey(credentials.key);
      setAuthenticated(true);
      clearExpired();
      onComplete();
    } catch (err) {
      setIsAuthenticating(false);
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        setError(t('setup.errorQrAuthFailed', 'Authentication failed. Check kiosk credentials.'));
      } else if (err instanceof SyntaxError) {
        setError(t('setup.errorQrInvalid', 'Invalid QR code format'));
      } else {
        setError(t('setup.errorQrScanFailed', 'QR authentication failed. Try manual key entry.'));
      }
    }
  };

  // Manual key entry submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!key.trim()) {
      setError(t('setup.errorEmpty', 'Please enter a kiosk key'));
      return;
    }

    setIsValidating(true);
    setError(null);

    try {
      // Save key without BFF auth (manual fallback — SignalR registration handles validation)
      setKioskKey(key.trim());
      clearExpired();
      onComplete();
    } catch (err) {
      console.error('Failed to validate kiosk key:', err);
      setError(t('setup.errorInvalid', 'Invalid kiosk key'));
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        {expired && (
          <MessageBar intent="warning" className={styles.sessionExpiredBanner}>
            <MessageBarBody>
              {t(
                'setup.sessionExpired',
                "This kiosk's session has expired. Please set the device up again — scan the setup QR code to continue."
              )}
            </MessageBarBody>
          </MessageBar>
        )}
        <div className={styles.header}>
          <Text className={styles.title}>
            <Key24Regular />
            {t('setup.title', 'Kiosk Setup')}
          </Text>
          <Text className={styles.subtitle}>
            {t('setup.subtitle', 'Scan the QR code provided by your administrator to set up this kiosk')}
          </Text>
        </div>

        {/* QR Scanner Section (primary flow) */}
        <div className={styles.qrSection}>
          <Text className={styles.qrSectionTitle} weight="semibold">
            <QrCode24Regular />
            {t('setup.qrScanTitle', 'Scan QR Code')}
          </Text>

          {isAuthenticating ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXL }}>
              <Spinner size="large" label={t('setup.authenticating', 'Authenticating...')} />
            </div>
          ) : (
            <div className={styles.qrContainer}>
              <div id="kiosk-qr-reader" ref={qrContainerRef} style={{ width: '100%' }} />
              {!scannerReady && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalL }}>
                  <Spinner size="small" label={t('setup.loadingCamera', 'Loading camera...')} />
                </div>
              )}
            </div>
          )}

          {error && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: tokens.spacingVerticalS }}>
              <Text className={styles.error} size={300}>
                {error}
              </Text>
              {scannerStopped && (
                <Button
                  appearance="primary"
                  icon={<QrCode24Regular />}
                  onClick={initScanner}
                >
                  {t('setup.retry', 'Retry Scan')}
                </Button>
              )}
            </div>
          )}

          {/* Paste code fallback (no camera needed) */}
          <form onSubmit={handleCodeSubmit} style={{ display: 'flex', gap: tokens.spacingHorizontalS }}>
            <Input
              style={{ flex: 1 }}
              value={codeInput}
              onChange={(_, data) => {
                setCodeInput(data.value);
                setError(null);
              }}
              placeholder={t('setup.codePlaceholder', 'Paste QR code here...')}
              disabled={isAuthenticating}
              appearance="filled-darker"
            />
            <Button
              type="submit"
              appearance="primary"
              icon={isAuthenticating ? <Spinner size="tiny" /> : <Checkmark24Regular />}
              disabled={isAuthenticating || !codeInput.trim()}
            >
              {t('setup.connect', 'Connect')}
            </Button>
          </form>
        </div>

        <Divider className={styles.divider}>
          {t('setup.orManual', 'Or enter key manually')}
        </Divider>

        {/* Manual Key Entry Section (fallback) */}
        <div className={styles.manualSection}>
          <form onSubmit={handleSubmit} className={styles.form}>
            <Field
              label={t('setup.keyLabel', 'Kiosk Key')}
              validationState={error ? 'error' : undefined}
              validationMessage={error || undefined}
            >
              <Input
                size="large"
                className={styles.input}
                value={key}
                onChange={(_, data) => {
                  setKey(data.value);
                  setError(null);
                }}
                placeholder={t('setup.keyPlaceholder', 'Enter key...')}
                disabled={isValidating || isAuthenticating}
                autoFocus={false}
              />
            </Field>

            <Button
              appearance="secondary"
              size="large"
              type="submit"
              className={styles.button}
              icon={isValidating ? <Spinner size="tiny" /> : <Checkmark24Regular />}
              disabled={isValidating || isAuthenticating || !key.trim()}
            >
              {isValidating
                ? t('setup.validating', 'Validating...')
                : t('setup.activate', 'Activate Kiosk')}
            </Button>
          </form>
        </div>
      </Card>
    </div>
  );
}

export default KioskSetup;
